import { useEffect, useState, useMemo } from 'react';
import { Avatar, Flex, Icon, Spinner } from '@chakra-ui/react';
import { colors } from '@/theme/foundations/colors';
import { useQuery } from '@tanstack/react-query';
import { CalendarCheck } from '@/assets/icons/interview';
import { KText } from '@/ui-library/components/text';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import { KAvatarItem, KButton } from '@/ui-library/components';
import { FeaturedIcon } from '@/assets/icons/ats';
import { ScheduleAPI } from '../api';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { KPopover } from '@/ui-library/components/organisms/KPopover';

const InterviewToSchedule = ({
  bulkScheduleProps,
  startSelfSchedule,
  onContactsChange,
}) => {
  const [selectedAction, setSelectedAction] = useState(null);
  const [applicationIds, setApplicationIds] = useState([]);
  const {
    selectedApplications,
    unSelectedApplications,
    jobStageId,
    jobStatus,
  } = bulkScheduleProps || {};

  const { data: interviewStatus, isLoading } = useQuery({
    queryKey: ['interviewStatus', bulkScheduleProps],
    queryFn: () =>
      ScheduleAPI?.getStageActionDetails({
        selectedApplicationIds: !unSelectedApplications
          ? selectedApplications?.map((application) => application.id)
          : undefined,
        unSelectedApplicationIds: unSelectedApplications,
        jobStageId,
        jobStatus,
      }),
    // enabled:
    //   !!bulkScheduleProps?.selectedApplications ||
    //   !!bulkScheduleProps?.unSelectedApplications,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const {
    ats_applications,
    interview_job_stage_actions,
    invalid_ats_applications,
  } = interviewStatus || {};

  const stageActions = useMemo(
    () => interview_job_stage_actions || [],
    [interview_job_stage_actions]
  );

  //set default stage action
  useEffect(() => {
    if (stageActions.length === 1) {
      setSelectedAction(stageActions[0]);
    }
  }, [stageActions]);

  useEffect(() => {
    const contacts = ats_applications?.map((application) => {
      const { id, first_name, last_name, image_url, position } =
        application.contact;
      return {
        id,
        name: `${first_name || ''} ${last_name || ''}`,
        avatar: image_url,
        position,
        showPosition: true,
      };
    });

    const noEmailContactIds =
      invalid_ats_applications?.[0]?.ats_applications?.map(
        (applicant) => applicant.contact.id
      ) || [];

    const existingApplicantIds =
      selectedAction?.existing_interview_applications?.map(
        (applicant) => applicant.contact.id
      ) || [];

    const invalidIds = [...noEmailContactIds, ...existingApplicantIds];
    const filteredContacts = contacts?.filter(
      (contact) => !invalidIds.includes(contact.id)
    );

    const filteredApplications = ats_applications?.filter(
      (application) => !invalidIds.includes(application.contact.id)
    );
    onContactsChange(filteredContacts);

    const eligibleApplicants = filteredApplications?.map(
      (application) => application.id
    );
    setApplicationIds(eligibleApplicants);

    //directly land on scheduling calendar as all candidates are eligible and only one stage action is available
    if (
      selectedAction &&
      ats_applications?.length === eligibleApplicants?.length &&
      interview_job_stage_actions?.length === 1
    ) {
      startSelfSchedule({
        applicationIds: eligibleApplicants,
        actionId: interview_job_stage_actions?.[0]?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ats_applications,
    invalid_ats_applications,
    onContactsChange,
    selectedAction?.existing_interview_applications,
  ]);

  const handleStageActionChange = (option) => {
    setSelectedAction(option);
  };

  const renderCandidatesTag = (candidate) => {
    const { first_name, last_name, image_url } = candidate?.contact || {};
    const name = `${first_name || ''} ${last_name || ''}`;
    return (
      <Flex
        alignItems="center"
        gap="8px"
        bg="white"
        p="6px 12px"
        maxW="fit-content"
        borderRadius="sm"
        whiteSpace="nowrap"
      >
        <Avatar
          className="avatar-xs"
          name={name}
          w="20px"
          h="20px"
          borderRadius="3px"
          src={image_url}
        />
        <TruncatedTextWithTooltip
          size="sm-medium"
          text={name}
          maxW="120px"
          enableTooltip={true}
        />
      </Flex>
    );
  };

  const renderInvalidCandidatesTag = ({
    candidates,
    bgColor = 'pastel-6-light',
    color = 'pastel-6-dark',
    context = 'have already scheduled or completed this interview and will not receive the self-schedule link',
  }) => {
    const countToVisible = 3;
    const visibleCandidates = candidates.slice(0, countToVisible);
    const hiddenCandidates = candidates.slice(countToVisible);
    const remainingCount = hiddenCandidates.length;

    const renderPlusMoreTriggerContent = () => {
      return (
        <Flex
          bg="white"
          width={'33px'}
          height="33px"
          alignItems="center"
          justifyContent="center"
          borderRadius={'6px'}
          border="1px solid #fff"
          cursor={'pointer'}
        >
          <KText color={'secondary-700'} fontWeight="550" fontSize={'12px'}>
            +{remainingCount}
          </KText>
        </Flex>
      );
    };

    return (
      <Flex
        bg={bgColor}
        borderRadius="sm"
        maxW="650px"
        p="16px"
        flexDir="column"
      >
        <Flex gap="11px">
          <Icon as={FeaturedIcon} w="38px" h="38px" color={color} />
          <Flex flexDir="column" gap="8px" width="calc(100% - 49px)">
            <KText color={color}>
              <strong>
                {candidates?.length === 1
                  ? '1 candidate'
                  : `${candidates?.length} candidates`}
              </strong>{' '}
              {context}
            </KText>
            <Flex gap="6px" width="100%" flexWrap="nowrap">
              {visibleCandidates?.map(renderCandidatesTag)}
              {remainingCount > 0 && (
                <KPopover
                  popoverProps={{ trigger: 'hover' }}
                  triggerElement={renderPlusMoreTriggerContent()}
                  children={hiddenCandidates.map((candidate, index) => {
                    const { first_name, last_name, image_url } =
                      candidate?.contact || {};
                    return (
                      <KAvatarItem
                        key={index}
                        user={candidate}
                        name={`${first_name || ''} ${last_name || ''}`}
                        src={image_url || null}
                        showName={true}
                        avatarProps={{
                          className: 'avatar-xs',
                          width: '24px',
                          height: '24px',
                          borderRadius: '6px',
                          border: '1px solid #fff',
                        }}
                        containerProps={{
                          p: '10px',
                          borderBottom: '1px solid #E9EBF4',
                          alignItems: 'center',
                          gap: '12px',
                        }}
                        size={'sm'}
                        lineHeight="1rem"
                      />
                    );
                  })}
                  showCloseButton={false}
                  popoverContentProps={{
                    p: '0',
                    maxHeight: '340px',
                    overflow: 'scroll',
                  }}
                  popoverBodyProps={{ p: '0px' }}
                ></KPopover>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  };

  return (
    <Flex
      h="full"
      w="full"
      alignItems="center"
      justifyContent="center"
      flexDir="column"
    >
      {isLoading ? (
        <Spinner color="primary-800" />
      ) : (
        <>
          <Flex
            flexDir="column"
            gap="24px"
            alignItems="center"
            flex="2"
            justifyContent="flex-end"
          >
            <Icon as={CalendarCheck} w="auto" h="200px" maxW="100%" />
            <KText variant="heading_2">
              {stageActions.length === 1 ? 'Interview' : 'Select the interview'}{' '}
              to schedule
            </KText>
            <Flex
              w="100%"
              justifyContent="center"
              flexDir={'column'}
              gap="12px"
            >
              <KSelect
                value={selectedAction}
                isDisabled={stageActions.length === 1}
                customStyles={{
                  control: (baseStyles) => ({
                    ...baseStyles,
                    backgroundColor:
                      stageActions.length === 1 && colors['secondary-25'],
                  }),
                }}
                options={stageActions}
                placeholder="Select interview"
                onChange={handleStageActionChange}
                components={{
                  ...(stageActions.length === 1
                    ? { DropdownIndicator: () => null }
                    : undefined),
                  IndicatorSeparator: null,
                }}
              />
              <KButton
                isDisabled={!selectedAction || applicationIds.length === 0}
                display="flex"
                alignSelf="center"
                onClick={() =>
                  startSelfSchedule({
                    applicationIds,
                    actionId: selectedAction.id,
                  })
                }
              >
                Start self-schedule
              </KButton>
            </Flex>
          </Flex>
          <Flex
            flex="1"
            justifyContent="flex-end"
            pb="24px"
            flexDir="column"
            my="8px"
            gap="8px"
          >
            {invalid_ats_applications?.[0]?.ats_applications?.length > 0 &&
              renderInvalidCandidatesTag({
                candidates: invalid_ats_applications?.[0]?.ats_applications,
                bgColor: 'pastel-1-light',
                color: 'pastel-1-dark',
                context:
                  "don't have an associated email address and will not receive the self-scheduling link",
              })}
            {selectedAction?.existing_interview_applications?.length > 0 &&
              renderInvalidCandidatesTag({
                candidates: selectedAction?.existing_interview_applications,
              })}
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default InterviewToSchedule;
